import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Collapse,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  InputAdornment,
  List,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  TextField,
  Typography,
  Alert,
} from "@mui/material";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import PublisherAccount from "../types/admob/PublisherAccount";
import { useState } from "react";
import AdMobAPI from "../util/api/AdMobAPI";

interface Props {
  accounts: PublisherAccount[];
  onSelect: (account: PublisherAccount) => void;
}

export default function AccountSelectorDialog({ accounts, onSelect }: Props) {
  const [showManual, setShowManual] = useState(accounts.length === 0);
  const [pubIdInput, setPubIdInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [manualError, setManualError] = useState<string | null>(null);

  // Normalise input — user may type "pub-123" or just "123"
  const buildAccountName = (input: string) => {
    const cleaned = input.trim();
    if (cleaned.startsWith("accounts/")) return cleaned;
    if (cleaned.startsWith("pub-")) return `accounts/${cleaned}`;
    return `accounts/pub-${cleaned}`;
  };

  const handleManualSubmit = async () => {
    if (!pubIdInput.trim()) return;
    setManualError(null);
    setLoading(true);
    try {
      const accountName = buildAccountName(pubIdInput);
      // Verify the pub ID works by fetching apps — lightweight call
      // We build a minimal PublisherAccount object from what the user gave us
      // and let the rest of the app use it normally
      const pubId = accountName.replace("accounts/", "");
      const syntheticAccount: PublisherAccount = {
        name: accountName,
        publisherId: pubId,
        reportingTimeZone: "—",
        currencyCode: "—",
      };
      // Quick validation — try listApps to confirm token + pub ID combo works
      await AdMobAPI.listApps(accountName);
      onSelect(syntheticAccount);
    } catch (err: any) {
      const msg: string = err?.message ?? "";
      if (msg.includes("401") || msg.includes("UNAUTHENTICATED")) {
        setManualError(
          "Authentication failed. This publisher ID is not accessible with your current login. Make sure your email has at least User access in that AdMob account."
        );
      } else if (msg.includes("403") || msg.includes("PERMISSION_DENIED")) {
        setManualError(
          "Permission denied. Your email does not have sufficient access to this publisher account."
        );
      } else if (msg.includes("404") || msg.includes("NOT_FOUND")) {
        setManualError(
          "Publisher ID not found. Double-check the ID and try again."
        );
      } else {
        setManualError(`Could not verify publisher ID: ${msg}`);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open fullWidth maxWidth="xs">
      <DialogTitle>
        <Typography variant="h6" fontWeight={600}>
          Select AdMob Account
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {accounts.length > 0
            ? "Choose a publisher account to continue."
            : "No accounts were auto-detected. Enter your Publisher ID manually."}
        </Typography>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ p: 0 }}>

        {/* ── Auto-fetched accounts list ── */}
        {accounts.length > 0 && (
          <List disablePadding>
            {accounts.map((acc, index) => (
              <ListItemButton
                key={acc.publisherId}
                onClick={() => onSelect(acc)}
                divider={index < accounts.length - 1}
                sx={{ py: 2, px: 3 }}
              >
                <ListItemAvatar>
                  <Avatar sx={{ bgcolor: "primary.main" }}>
                    <AccountCircleIcon />
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Typography fontWeight={500}>
                      {acc.publisherId}
                    </Typography>
                  }
                  secondary={
                    <Box
                      component="span"
                      sx={{ display: "flex", flexDirection: "column", gap: 0.3, mt: 0.5 }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        Currency: {acc.currencyCode}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Timezone: {acc.reportingTimeZone}
                      </Typography>
                    </Box>
                  }
                />
              </ListItemButton>
            ))}
          </List>
        )}

        {/* ── "Add manually" toggle button — only shown when auto list exists ── */}
        {accounts.length > 0 && (
          <>
            <Divider />
            <Box sx={{ px: 3, py: 1.5 }}>
              <Button
                size="small"
                startIcon={<AddCircleOutlineIcon />}
                onClick={() => {
                  setShowManual((v) => !v);
                  setManualError(null);
                  setPubIdInput("");
                }}
              >
                {showManual ? "Cancel manual entry" : "Enter Publisher ID manually"}
              </Button>
            </Box>
          </>
        )}

        {/* ── Manual input section ── */}
        <Collapse in={showManual}>
          <Box sx={{ px: 3, pb: 3, pt: accounts.length === 0 ? 2 : 0, display: "flex", flexDirection: "column", gap: 2 }}>

            {/* Error reasons — shown only when auto-fetch failed (accounts empty) */}
            {accounts.length === 0 && (
              <Alert severity="warning" sx={{ mb: 1 }}>
                <Typography variant="caption" display="block" fontWeight={500} sx={{ mb: 0.5 }}>
                  Why auto-detection failed?
                </Typography>
                <Box component="ul" sx={{ mt: 0, pl: 2, mb: 0 }}>
                  <li><Typography variant="caption">Your email is not the primary owner/admin of any AdMob account</Typography></li>
                  <li><Typography variant="caption">Your email is not added as Test User in Google Cloud OAuth consent screen</Typography></li>
                  <li><Typography variant="caption">AdMob account is suspended or inactive</Typography></li>
                </Box>
              </Alert>
            )}

            <TextField
              label="Publisher ID"
              placeholder="pub-1234567890123456"
              value={pubIdInput}
              onChange={(e) => {
                setPubIdInput(e.target.value);
                setManualError(null);
              }}
              onKeyDown={(e) => e.key === "Enter" && handleManualSubmit()}
              size="small"
              fullWidth
              error={!!manualError}
              helperText={
                manualError
                  ? manualError
                  : 'Enter your AdMob Publisher ID, e.g. "pub-1234567890123456"'
              }
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Typography variant="caption" color="text.secondary">
                      accounts/
                    </Typography>
                  </InputAdornment>
                ),
              }}
            />

            <Button
              variant="contained"
              onClick={handleManualSubmit}
              disabled={!pubIdInput.trim() || loading}
              startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
            >
              {loading ? "Verifying…" : "Continue with this Account"}
            </Button>
          </Box>
        </Collapse>

      </DialogContent>
    </Dialog>
  );
}
